// src/tenantAdmin/components/UserRowActions.jsx
//
// The controls of ONE member row of the Utenti tab. Presentation only: it decides
// WHICH controls a row's state earns and calls back; the page does the writes.
//
// ⛔⛔ THE STATE→CONTROL MAPPING IS THE POINT OF THIS COMPONENT:
//
//   is_active                        → Disattiva          (disabled on your own row)
//   !is_active &&  pending_invite    → Reinvia invito     (a new LINK; no password exists)
//   !is_active && !pending_invite    → Riattiva           (restores a credential that exists)
//
// The two halves of `!is_active` are NOT interchangeable and the backend enforces
// it: activating someone who never onboarded answers 409 USER_NEVER_ONBOARDED, and
// resending to someone who did answers 409 USER_NOT_PENDING_INVITE. Offering the
// wrong one is a control that can only fail, which is why the row picks exactly one.
//
// ⛔ NEVER RENDERED FOR A `via:'customer'` ROW. Those people administer the
// workspace through the COMPANY and own no membership row in it, so every
// per-user route 404s on them (`removable: false`) — they live in
// CompanyAdminsBlock, read-only. The caller enforces this by construction (the
// hook splits them out of `users` before the table ever sees them).
//
// ⚠️ Riattiva is disabled ONLY at a KNOWN cap (`limit != null && used >= limit`).
// An UNKNOWN figure (the seat read failed, or an older payload) leaves it ENABLED:
// a control greyed on missing data is indistinguishable from one greyed on a real
// refusal, and the 409 USER_SEAT_LIMIT_REACHED backstop still holds. Degrade open.
//
// ⚠️ "Correggi email" is absent, not greyed, on your OWN row: the backend answers
// 409 CANNOT_EDIT_OWN_EMAIL, and your own address is changed from Profilo.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

const rowActionBtn = (variant) => ({
  padding: '8px 16px', borderRadius: RADII.control, fontSize: '12px', fontWeight: 600,
  fontFamily: FONT_SANS, whiteSpace: 'nowrap',
  ...(variant === 'danger'
    ? { border: 'none', background: SEMANTIC.errore.fg, color: COLORS.surface }
    : { border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface, color: COLORS.ink }),
});

// One place for the greyed-out look, so a disabled control never differs by row.
const muted = (disabled) => ({
  opacity: disabled ? 0.5 : 1,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

export default function UserRowActions({
  row,
  isSelf,
  seats,
  onResend,
  onReactivate,
  onDeactivate,
  onCorrectEmail,
  onRemove,
}) {
  const { t } = useTranslation('admin');

  const pendingInvite = !row.is_active && !!row.pending_invite;
  const deactivated = !row.is_active && !row.pending_invite;
  // ⛔ A KNOWN cap only — see the header note.
  const atCap = seats?.limit != null && seats?.used != null && seats.used >= seats.limit;

  return (
    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
      {row.is_active && (
        <button
          onClick={() => { if (!isSelf) onDeactivate(row); }}
          disabled={isSelf}
          title={isSelf ? t('tenantUsers.cannotDeactivateSelf') : undefined}
          style={{ ...rowActionBtn('outline'), ...muted(isSelf) }}
        >
          {t('users.deactivate')}
        </button>
      )}

      {pendingInvite && (
        <button
          onClick={() => onResend(row)}
          style={{ ...rowActionBtn('outline'), ...muted(false) }}
        >
          {t('manage.resend')}
        </button>
      )}

      {deactivated && (
        <button
          onClick={() => { if (!atCap) onReactivate(row); }}
          disabled={atCap}
          title={atCap ? t('manage.reactivateAtCap') : undefined}
          style={{ ...rowActionBtn('outline'), ...muted(atCap) }}
        >
          {t('manage.reactivate')}
        </button>
      )}

      {!isSelf && (
        <button
          onClick={() => onCorrectEmail(row)}
          style={{ ...rowActionBtn('outline'), ...muted(false) }}
        >
          {t('manage.correctEmail')}
        </button>
      )}

      <button
        onClick={() => { if (!isSelf) onRemove(row); }}
        disabled={isSelf}
        title={isSelf ? t('tenantUsers.cannotRemoveSelf') : undefined}
        style={{ ...rowActionBtn('danger'), ...muted(isSelf) }}
      >
        {t('shared.remove')}
      </button>
    </div>
  );
}
